/**
 * Sheet metal staging area — the engineering step before a metals project.
 *
 * A job holds the STEP files dropped for one piece of work, the sheet metal
 * parts found inside them, and the gauge and forming decisions made about each.
 * When everything is configured it converts into a MetalsProject, exactly as an
 * Onshape Bridge document used to, and stops being edited.
 *
 * Geometry is never stored here. The STEP file is the source of truth and the
 * client re-derives from it; what a job keeps is the decisions a person made,
 * which is the only part that cannot be recomputed.
 */
export enum SheetMetalJobStatus {
    /** Being configured. Parts may still be missing gauges or a sequence. */
    Draft = 'Draft',
    /** Every part is configured — ready to convert. */
    Ready = 'Ready',
    /** Converted into a metals project; kept for reference. */
    Converted = 'Converted',
}

export interface SheetMetalJob {
    id: string;
    name: string;
    customerId: string | null;
    status: SheetMetalJobStatus;
    /** Set once converted, so the project can be reached from here. */
    metalsProjectId: string | null;
    notes: string;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}

/**
 * One STEP file dropped into a job.
 *
 * A file may hold a single part or a whole assembly, so parts point back here
 * rather than the other way round. Parsing happens in the browser, so a file
 * that fails to parse keeps its error for the person who dropped it.
 */
export enum SheetMetalSourceFileStatus {
    Pending = 'Pending',
    Parsed = 'Parsed',
    Failed = 'Failed',
}

export interface SheetMetalSourceFile {
    id: string;
    jobId: string;
    name: string;
    /** Appwrite storage id of the uploaded STEP. */
    stepFileId: string;
    sizeBytes: number;
    status: SheetMetalSourceFileStatus;
    /** How many sheet metal solids came out of it. */
    partsFound: number;
    /** Solids that were skipped because they are not sheet metal. */
    solidsSkipped: number;
    error: string | null;
    uploadedOn: Date;
    uploadedById: string;
}
